import {useCallback, useMemo, useState, type RefObject} from 'react'
import {SelectPanel, type SelectPanelItemInput, type SelectPanelProps} from '@primer/react'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {useListUpdateMutation} from '../hooks/use-list-update-mutation'
import {CreateListDialog} from './CreateListDialog'
import type {StarState} from '../types'
import {useUserListsQuery} from '../hooks/use-user-lists-query'

type StarListSelectItem = SelectPanelItemInput & {
  id: string
}

export interface StarListMenuProps {
  anchorRef: RefObject<HTMLButtonElement | null>
  listsApiPath: string
  onClose: () => void
  repoStarred: boolean
  starCount: number
  listsUpdatePath: string
  onStarStateChange: (next: StarState) => void
}

function parseListId(itemId: string): number {
  return Number(itemId.replace('list-', ''))
}

export function StarListMenu({
  anchorRef,
  onClose,
  repoStarred,
  starCount,
  listsApiPath,
  listsUpdatePath,
  onStarStateChange,
}: StarListMenuProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [filter, setFilter] = useState('')
  const [localSelectedIds, setLocalSelectedIds] = useState<Set<number> | null>(null)
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const {listsData, isLoading, isFetching, isError} = useUserListsQuery({
    listsApiPath,
    enabled: !showCreateDialog,
  })

  // Server-derived selected IDs — used until the user makes a local change
  const serverSelectedIds = useMemo(
    () => new Set(listsData?.lists.filter(list => list.repoIncluded).map(list => list.id) ?? []),
    [listsData],
  )

  const selectedIds = localSelectedIds ?? serverSelectedIds

  const visibleItems = useMemo<StarListSelectItem[]>(
    () =>
      (listsData?.lists ?? [])
        .filter(list => list.name.toLowerCase().includes(filter.toLowerCase()))
        .map(list => ({
          id: `list-${list.id}`,
          text: list.name,
          description: list.description,
          descriptionVariant: 'block',
        })),
    [filter, listsData],
  )

  const selectedItems = useMemo<StarListSelectItem[]>(
    () => visibleItems.filter(item => selectedIds.has(parseListId(item.id))),
    [selectedIds, visibleItems],
  )

  const listUpdateMutation = useListUpdateMutation({
    listsApiPath,
    listsUpdatePath,
    repoStarred,
    starCount,
    onStarStateChange,
  })
  const {mutate: updateLists} = listUpdateMutation

  const isDirty = localSelectedIds !== null

  const handleSelectedChange = useCallback((selected: SelectPanelItemInput | SelectPanelItemInput[]) => {
    const nextSelected = Array.isArray(selected) ? selected : selected ? [selected] : []
    const nextIds = new Set(nextSelected.map(item => parseListId(String(item.id))).filter(Number.isInteger))
    setLocalSelectedIds(nextIds)
  }, [])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && isDirty) {
        sendClickAnalyticsEvent({
          category: 'REPOSITORY_OVERVIEW',
          action: 'click:star_lists_save',
          label: `location:header;ref_cta:star_lists_panel;list_count:${selectedIds.size}`,
        })
        updateLists({listIds: [...selectedIds]})
      }
      if (!nextOpen) {
        onClose()
      }
    },
    [isDirty, onClose, selectedIds, sendClickAnalyticsEvent, updateLists],
  )

  let message: SelectPanelProps['message'] | undefined = undefined

  if (isError) {
    message = {
      variant: 'error',
      title: 'Lists could not be loaded',
      body: 'Try again later.',
    }
  } else if (!isLoading && !isFetching) {
    if (listsData?.lists.length === 0 && listsData.canCreateList) {
      message = {
        variant: 'empty',
        title: 'No lists yet',
        body: 'Create a list to organize your starred repositories.',
      }
    } else if (visibleItems.length === 0) {
      message = {
        variant: 'empty',
        title: 'No matching lists',
        body: 'Try a different search term.',
      }
    }
  }

  if (showCreateDialog) {
    return (
      <CreateListDialog
        createPath={listsData?.createPath ?? '/stars/lists'}
        listsApiPath={listsApiPath}
        onClose={onClose}
        repositoryId={listsData?.repositoryId}
        starCount={starCount}
        onStarStateChange={onStarStateChange}
      />
    )
  }

  return (
    <SelectPanel
      title="Lists"
      subtitle="Add this repository to one or more lists"
      aria-label="Add this repository to a list"
      anchorRef={anchorRef}
      renderAnchor={null}
      open
      onOpenChange={handleOpenChange}
      onCancel={onClose}
      items={visibleItems}
      selected={selectedItems}
      onSelectedChange={handleSelectedChange}
      onFilterChange={setFilter}
      placeholderText="Filter lists"
      inputLabel="Filter lists"
      loading={isLoading || isFetching}
      message={message}
      secondaryAction={
        listsData?.canCreateList ? (
          <SelectPanel.SecondaryActionButton
            onClick={() => {
              sendClickAnalyticsEvent({
                category: 'REPOSITORY_OVERVIEW',
                action: 'click:star_lists_create',
                label: 'location:header;ref_cta:star_lists_panel',
              })
              setShowCreateDialog(true)
            }}
          >
            Create list
          </SelectPanel.SecondaryActionButton>
        ) : undefined
      }
      overlayProps={{width: 'medium'}}
    />
  )
}
